<template>
  <div class="kpi-vendas">
    <!-- Hero cards -->
    <div class="kpi-grid kpi-grid--3" v-if="cards">
      <div class="hero-card variant-primary">
        <div class="hero-indicator"></div>
        <div class="hero-body">
          <div class="hero-number">{{ formatMoney(cards.meta_faturamento) }}</div>
          <div class="hero-label">{{ t('Meta vendas mensal') }}</div>
          <div class="hero-footer">
            <span :class="pctClass(cards.percentual_atingimento)">{{ fmtPct(cards.percentual_atingimento) }}</span>
            <span class="hero-footer-label">{{ t('atingido') }}</span>
          </div>
        </div>
      </div>
      <div class="hero-card variant-info">
        <div class="hero-indicator"></div>
        <div class="hero-body">
          <div class="hero-number">{{ formatMoney(cards.previsao_vendas) }}</div>
          <div class="hero-label">{{ t('Previsão de vendas') }}</div>
          <div class="hero-footer">
            <span class="hero-footer-value">{{ formatMoney(cards.ticket_medio_item) }}</span>
            <span class="hero-footer-label">{{ t('/item') }}</span>
          </div>
        </div>
      </div>
      <div class="hero-card variant-success">
        <div class="hero-indicator"></div>
        <div class="hero-body">
          <div class="hero-number">{{ formatMoney(cards.vendas_realizadas) }}</div>
          <div class="hero-label">{{ t('Vendas realizadas') }}</div>
          <div class="hero-footer">
            <span class="hero-footer-value">{{ cards.clientes_atendidos }}</span>
            <span class="hero-footer-label">{{ t('clientes') }}</span>
            <span class="hero-dot">·</span>
            <span class="hero-footer-value">{{ fmtDec(cards.itens_por_cliente) }}</span>
            <span class="hero-footer-label">{{ t('itens/cliente') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-else-if="loading" class="sk-grid">
      <DashboardSkeletonBlock height="80px" radius="10px" v-for="n in 3" :key="'h-'+n" />
    </div>

    <!-- Error -->
    <div v-else-if="error" class="error-state">
      <p>{{ t('Erro ao carregar dados de vendas') }}</p>
      <Button :label="t('Tentar novamente')" @click="fetchAll" data-testid="vendas-kpi-retry" />
    </div>

    <!-- Secondary cards -->
    <div class="kpi-grid kpi-grid--6 kpi-secondary-row" v-if="cards">
      <StatsCard :value="fmtDec(cards.meta_itens_dia)" :label="t('Meta itens/dia')" icon="pi pi-flag" variant="primary" compact />
      <StatsCard :value="fmtDec(cards.media_diaria_realizada)" :label="t('Média diária')" icon="pi pi-calendar" variant="info" compact />
      <StatsCard :value="cards.itens_em_atraso" :label="t('Itens atrasados')" icon="pi pi-exclamation-triangle" :variant="cards.itens_em_atraso > 0 ? 'danger' : 'success'" compact />
      <StatsCard :value="formatMoney(cards.ticket_medio_geral)" :label="t('Ticket médio')" icon="pi pi-ticket" variant="warning" compact />
      <StatsCard :value="fmtDec(cards.itens_por_cliente)" :label="t('Itens por cliente')" icon="pi pi-users" variant="primary" compact />
      <!--
        RECEBIDO é CAIXA, não venda: inclui pagamento de OS de meses anteriores
        e exclui OS do mês ainda não paga. Por isso card próprio, ao lado de
        "Vendas realizadas", e nunca no lugar dele.
      -->
      <StatsCard :value="formatMoney(cards.recebido_no_periodo)" :label="t('Recebido no período')" icon="pi pi-wallet" variant="success" compact />
    </div>

    <!-- Charts -->
    <div class="charts-row" v-if="evolucao && evolucao.length">
      <charts type="area" :series="itensVsVendasSeries" :options="itensVsVendasOptions" height="280px" wrapper-class="chart-box" />
      <charts type="bar" :series="vendasDiariasSeries" :options="vendasDiariasOptions" height="280px" wrapper-class="chart-box" />
    </div>

    <div class="recorrencia-row" v-if="recorrencia || (fidelizados && fidelizados.length)">
      <div class="hero-card variant-success" v-if="recorrencia" data-testid="taxa-recorrencia-card">
        <div class="hero-indicator"></div>
        <div class="hero-body">
          <div class="hero-number" data-testid="taxa-recorrencia-valor">{{ recorrencia.total_clientes > 0 ? fmtPct(recorrencia.taxa) : '—' }}</div>
          <div class="hero-label">
            {{ t('Taxa de recorrência') }}
            <InfoTip :explanation="EX.taxaRecorrencia" :label="t('Taxa de recorrência')" test-id="taxa-recorrencia-info" />
          </div>
          <div class="hero-footer" data-testid="taxa-recorrencia-rodape">
            <template v-if="recorrencia.total_clientes > 0">
              <span class="hero-footer-value">{{ recorrencia.clientes_recorrentes }}</span>
              <span class="hero-footer-label">{{ t('de') }}</span>
              <span class="hero-footer-value">{{ recorrencia.total_clientes }}</span>
              <span class="hero-footer-label">{{ t('clientes') }}</span>
            </template>
            <span class="hero-footer-label" v-else>{{ t('Sem clientes no período') }}</span>
          </div>
        </div>
      </div>
      <div class="chart-box" v-if="fidelizados && fidelizados.length">
        <charts type="bar" :series="fidelizadosSeries" :options="fidelizadosOptions" height="280px" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import DashboardSkeletonBlock from '../../widgets/DashboardSkeletonBlock.vue';
import StatsCard from '../../../../ui/StatsCard.vue';
import { useDashboardVendasKpi } from '../../../../composables/dashboard/useDashboardVendasKpi';
import { useChartFormat } from '../../../../composables/useChartFormat';
import { vendasKpiExplanations } from '../../explanations';

const { t, n } = useI18n();
const { currencySymbol, monthLabel, dayLabel } = useChartFormat();
const { cards, evolucaoDiaria, fidelizados, recorrencia, isLoading, error, fetchAll } = useDashboardVendasKpi();
const EX = computed(() => vendasKpiExplanations(t));

const loading = isLoading;
const evolucao = evolucaoDiaria;

function formatMoney(c: number) { return n(c / 100, 'currency'); }
function fmtDec(v: number) { return n(v / 100, 'decimal'); }
function fmtPct(bp: number) { return `${(bp / 100).toFixed(1)}%`; }
function pctClass(bp: number) {
  if (bp >= 10000) return 'text-success';
  if (bp >= 5000) return 'text-warning';
  return 'text-danger';
}

const itensVsVendasSeries = computed(() => {
  const pts = evolucao.value;
  if (!pts?.length) return [];
  return [
    { name: t('Itens recebidos'), data: pts.map(p => p.itens_recebidos ?? 0) },
    { name: t('Vendas realizadas'), data: pts.map(p => Math.round((p.vendas_realizadas ?? 0) / 100)) },
  ];
});

const itensVsVendasOptions = computed(() => ({
  chart: { stacked: false },
  xaxis: { categories: evolucao.value?.map(p => dayLabel(p.data)) ?? [], labels: { style: { fontSize: '11px' } } },
  yaxis: { title: { text: t('Quantidade') }, labels: { style: { fontSize: '11px' } } },
  colors: ['var(--p-primary-500)', 'var(--p-green-500)'],
  // design-token: ApexCharts JS accepts CSS var() in config arrays
  stroke: { curve: 'smooth' as const, width: 2 },
  fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.1 } },
  dataLabels: { enabled: false },
  tooltip: { shared: true, intersect: false },
  legend: { fontSize: '12px' },
}));

const vendasDiariasSeries = computed(() => {
  const pts = evolucao.value;
  if (!pts?.length) return [];
  return [{ name: t('Vendas realizadas'), data: pts.map(p => Math.round((p.vendas_realizadas ?? 0) / 100)) }];
});

const vendasDiariasOptions = computed(() => ({
  chart: { stacked: false },
  xaxis: { categories: evolucao.value?.map(p => dayLabel(p.data)) ?? [], labels: { style: { fontSize: '11px' } } },
  yaxis: { title: { text: currencySymbol() }, labels: { style: { fontSize: '11px' } } },
  colors: ['var(--p-green-500)'],
  // design-token: ApexCharts JS accepts CSS var() in config arrays
  dataLabels: { enabled: false },
  legend: { fontSize: '12px' },
}));

const fidelizadosSeries = computed(() => {
  const ms = fidelizados.value;
  if (!ms?.length) return [];
  return [
    { name: t('Clientes atendidos'), data: ms.map(m => m.total_clientes) },
    { name: t('Clientes fidelizados'), data: ms.map(m => m.clientes_fidelizados) },
  ];
});

const fidelizadosOptions = computed(() => ({
  chart: { stacked: false },
  xaxis: { categories: fidelizados.value?.map(m => monthLabel(m.mes)) ?? [], labels: { style: { fontSize: '11px' } } },
  yaxis: { title: { text: t('Clientes') }, labels: { style: { fontSize: '11px' } } },
  colors: ['var(--p-primary-500)', 'var(--p-amber-500)'],
  // design-token: ApexCharts JS accepts CSS var() in config arrays
  dataLabels: { enabled: false },
  tooltip: { shared: true, intersect: false },
  legend: { fontSize: '12px' },
}));
</script>

<style scoped>
.kpi-grid { display: grid; gap: 1.25rem; }
.kpi-grid--3 { grid-template-columns: repeat(3, 1fr); }
.kpi-grid--5 { grid-template-columns: repeat(5, 1fr); }
.kpi-grid--6 { grid-template-columns: repeat(6, 1fr); }
@media (max-width: 1100px) { .kpi-grid--3 { grid-template-columns: repeat(2, 1fr); } .kpi-grid--5, .kpi-grid--6 { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 900px) { .kpi-grid--3, .kpi-grid--5, .kpi-grid--6 { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 500px) { .kpi-grid--3, .kpi-grid--5, .kpi-grid--6 { grid-template-columns: 1fr; } }

.hero-card {
  background: var(--p-surface-card); border: 1px solid var(--p-surface-border);
  border-radius: 8px; padding: 12px 14px; position: relative; overflow: hidden;
  transition: all 0.2s ease;
}
.hero-card:hover { transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,0,0,0.04); }
.hero-indicator { position: absolute; top: 0; left: 0; right: 0; height: 2px; }
.variant-primary .hero-indicator { background: linear-gradient(90deg, var(--p-primary-400), var(--p-primary-600)); }
.variant-info .hero-indicator    { background: linear-gradient(90deg, var(--p-primary-500), var(--p-primary-600)); }
.variant-success .hero-indicator { background: linear-gradient(90deg, var(--p-green-500), var(--p-green-600)); }
.hero-body { display: flex; flex-direction: column; gap: 1px; }
.hero-number { font-size: 18px; font-weight: 700; line-height: 1.3; color: var(--p-text-color); }
.hero-label { font-size: 10px; color: var(--p-text-color-secondary); font-weight: 500; text-transform: uppercase; letter-spacing: 0.4px; }
.hero-footer { margin-top: 3px; display: flex; align-items: center; gap: 3px; flex-wrap: wrap; font-size: 11px; }
.hero-footer-value { font-weight: 600; color: var(--p-text-color); }
.hero-footer-label { color: var(--p-text-color-secondary); }
.hero-dot { color: var(--p-text-color-secondary); }
.text-success { color: var(--p-green-600); }
.text-warning { color: var(--p-amber-600); }
.text-danger  { color: var(--p-red-600); }

.charts-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; margin-top: 1.25rem; }
@media (max-width: 1100px) { .charts-row { grid-template-columns: 1fr; } }
.chart-box { background: var(--p-surface-card); border: 1px solid var(--p-surface-border); border-radius: 8px; padding: 0.75rem; }
.recorrencia-row { display: grid; grid-template-columns: minmax(180px, 240px) minmax(0, 1fr); gap: 1.25rem; align-items: start; margin-top: 1.25rem; }
@media (max-width: 900px) { .recorrencia-row { grid-template-columns: 1fr; } }

.sk-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; }

.kpi-secondary-row { margin-top: 1.25rem; }

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  color: var(--p-text-color-secondary);
  text-align: center;
  gap: 1rem;
}
</style>
